import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { GetEmailTemplatesRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getEmailTemplatesRPC";
import { GetAllEmailTemplatesRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getAllEmailTemplatesRPC";
import { getAPIClient } from "../../networking/APIClient";
import {
  createEmailTemplateModel,
  EmailTemplateModel,
} from "../models/EmailTemplateModel";
import { LeoRPCResult } from "@surya-digital/leo-ts-runtime";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import { TypesSortOrder } from "@pulse/pulse-rpcs/lib/types/typesSortOrder";
import { createBoundedNameModel } from "../../models/BoundedNameModel";
import {
  useGetAllEmailTemplatesRPCClientImpl,
  useGetEmailTemplatesRPCClientImpl,
} from "../rpcs/RPC";
import { LeoUUID } from "@surya-digital/leo-ts-types";

export const EMAIL_TEMPLATES_PER_PAGE = 10;

const getInitialState = (): SnapshotIn<typeof EmailTemplatesStore> => ({
  emailTemplates: [],
  totalItems: 0,
  emptySearchQueryResult: false,
  loadingEmailTemplates: true,
  loadingAllEmailTemplates: false,
  getEmailTemplatesRPCError: null,
});

export const EmailTemplatesStore = types
  .model("EmailTemplatesStore", {
    emailTemplates: types.array(EmailTemplateModel),
    totalItems: types.integer,
    emptySearchQueryResult: types.boolean,
    loadingEmailTemplates: types.boolean,
    loadingAllEmailTemplates: types.boolean,
    getEmailTemplatesRPCError: types.maybeNull(
      types.enumeration(
        "GetEmailTemplatesRPCError",
        Object.values(GetEmailTemplatesRPC.RPCError),
      ),
    ),
    getAllEmailTemplatesRPCError: types.maybeNull(
      types.enumeration(
        "GetAllEmailTemplatesRPCError",
        Object.values(GetAllEmailTemplatesRPC.RPCError),
      ),
    ),
  })
  .actions((store) => ({
    getEmailTemplates: flow(function* (
      searchQuery = EMPTY_CHARACTER,
      pageIndex = 1,
      sortOrder = TypesSortOrder.TypesSortOrder.DESCENDING,
      sortColumn = GetEmailTemplatesRPC.RequestEnums.SortColumn.SortColumn
        .MODIFIED_AT,
      loadingOptionsChange = false,
    ) {
      if (!loadingOptionsChange) store.loadingEmailTemplates = true;

      try {
        store.getEmailTemplatesRPCError = null;

        const apiClient = getAPIClient(store);
        const trimmedSearchQuery = searchQuery.trim();
        const request = new GetEmailTemplatesRPC.Request(
          pageIndex - 1,
          EMAIL_TEMPLATES_PER_PAGE,
          sortOrder,
          sortColumn,
          trimmedSearchQuery.length ? trimmedSearchQuery : null,
        );

        const result: LeoRPCResult<
          GetEmailTemplatesRPC.Response,
          GetEmailTemplatesRPC.Errors.Errors
        > = yield useGetEmailTemplatesRPCClientImpl(apiClient).execute(request);
        if ("response" in result) {
          const emailTemplates = result.response.templates.map((template) =>
            createEmailTemplateModel({
              id: template.id.uuid,
              templateName: createBoundedNameModel({
                name: template.templateName.name,
              }),
              projectId: template.project?.id.uuid ?? null,
              projectName: template.project?.projectName.name,
            }),
          );
          store.emailTemplates.replace(emailTemplates);
          store.totalItems = result.response.totalItems;
          store.emptySearchQueryResult = !!(
            !emailTemplates.length && searchQuery.trim()
          );
        } else {
          switch (result.error.code) {
            case GetEmailTemplatesRPC.RPCError.InvalidPageIndex:
              store.getEmailTemplatesRPCError = result.error.code;
              break;
            default:
              result.error.code satisfies never;
          }
        }
      } finally {
        if (!loadingOptionsChange) store.loadingEmailTemplates = false;
      }
    }),
    getAllEmailTemplates: flow(function* (projectId: string) {
      store.loadingAllEmailTemplates = true;

      try {
        const apiClient = getAPIClient(store);
        const request = new GetAllEmailTemplatesRPC.Request(
          new LeoUUID(projectId),
        );

        const result: LeoRPCResult<
          GetAllEmailTemplatesRPC.Response,
          GetAllEmailTemplatesRPC.Errors.Errors
        > =
          yield useGetAllEmailTemplatesRPCClientImpl(apiClient).execute(
            request,
          );
        if ("response" in result) {
          const emailTemplates = result.response.templates.map((template) =>
            createEmailTemplateModel({
              id: template.id.uuid,
              templateName: createBoundedNameModel({
                name: template.templateName.name,
              }),
              projectId: template.project?.id.uuid ?? null,
              projectName: template.project?.projectName.name,
            }),
          );
          store.emailTemplates.replace(emailTemplates);
        } else {
          switch (result.error.code) {
            case GetAllEmailTemplatesRPC.RPCError.InvalidProjectId:
              store.getAllEmailTemplatesRPCError = result.error.code;
              break;
            default:
              result.error.code satisfies never;
          }
        }
      } finally {
        store.loadingAllEmailTemplates = false;
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createEmailTemplatesStore = (): Instance<
  typeof EmailTemplatesStore
> => EmailTemplatesStore.create(getInitialState());
